import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  signal
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';

import { PurchaseOrderPriority } from '../../../models/PurchaseOrder';
import {
  PurchaseOrderService,
  CreatePurchaseRequestPayload,
  PurchaseRequestSimpleViewResponse,
  UpdatePurchaseRequestItemPayload,
  UpdatePurchaseRequestPayload
} from '../../../services/purchase-order.service';
import { AuthService } from '../../../services/auth.service';
import { StaffService } from '../../../services/staff.service';
import { CostCentersService } from '../../../services/cost-centers.service';
import { StaffMember } from '../../../models/StaffMember';
import { ErrorService } from '../../../shared/services/error.service';

export interface SolicitacaoItem {
  id: string;
  descricao: string;
  qtd: number;
  unidade: string;
  justificativa: string;
}

type PriorityKey = 'baixa' | 'normal' | 'alta' | 'urgente';

@Component({
  selector: 'app-purchase-request-form',
  templateUrl: './purchase-request-form.component.html',
  styleUrls: ['./purchase-request-form.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PurchaseRequestFormComponent implements OnInit {
  description = signal('');
  notes = signal('');
  priorityKey = signal<PriorityKey>('normal');
  itens = signal<SolicitacaoItem[]>([this.novoItemVazio()]);

  isSaving = signal(false);
  isLoading = signal(false);
  error = signal<string | null>(null);
  resolvedCostCenterId = signal<number | null>(null);
  costCenterLabel = signal<string | null>(null);
  submitAttempted = signal(false);

  /** Modo edição quando a rota tem `:id` (`/purchase-requests/:id/edit`). */
  editingId = signal<number | null>(null);
  isEditMode = computed(() => this.editingId() != null);

  pageTitle = computed(() =>
    this.isEditMode() ? 'Editar solicitação de compra' : 'Nova solicitação de compra'
  );

  submitButtonLabel = computed(() =>
    this.isEditMode() ? 'Salvar alterações' : 'Enviar solicitação'
  );

  /** IDs originais dos itens carregados (mapeados por `SolicitacaoItem.id`) — usados no PUT. */
  private originalItemIds = new Map<string, number>();

  isFormValid = computed(() => {
    if (!this.description().trim()) {
      return false;
    }
    for (const it of this.itens()) {
      if (!it.descricao.trim()) {
        return false;
      }
      if (!it.unidade.trim()) {
        return false;
      }
      if (it.qtd < 1 || !Number.isFinite(it.qtd)) {
        return false;
      }
    }
    return true;
  });

  showCostCenterBanner = computed(() => {
    const id = this.resolvedCostCenterId();
    return id != null && id > 0 && !this.error();
  });

  private readonly priorityToApi: Record<PriorityKey, string> = {
    baixa: PurchaseOrderPriority.BAIXA,
    normal: PurchaseOrderPriority.NORMAL,
    alta: PurchaseOrderPriority.ALTA,
    urgente: PurchaseOrderPriority.URGENTE
  };

  constructor(
    private purchaseOrderService: PurchaseOrderService,
    private authService: AuthService,
    private staffService: StaffService,
    private costCentersService: CostCentersService,
    private router: Router,
    private route: ActivatedRoute,
    private errorService: ErrorService
  ) {}

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    const parsedId = idParam ? Number(idParam) : null;
    if (parsedId && Number.isFinite(parsedId) && parsedId > 0) {
      this.editingId.set(parsedId);
      this.loadForEdit(parsedId);
      return;
    }

    this.resolveCostCenterContext()
      .pipe(take(1))
      .subscribe(({ id, label }) => {
        this.resolvedCostCenterId.set(id);
        this.costCenterLabel.set(
          label?.trim()
            ? label.trim()
            : id != null && id > 0
              ? 'Centro de custo'
              : null
        );
        if (!id) {
          this.error.set(
            'Não foi possível identificar o centro de custo desta solicitação. Peça ao administrador para conferir seu cadastro.'
          );
        }
      });
  }

  /**
   * Carrega solicitação existente no modo edição via simple-view e hidrata o form.
   * Em caso de erro, redireciona pra lista (com toast).
   */
  private loadForEdit(id: number): void {
    this.isLoading.set(true);
    this.error.set(null);
    this.purchaseOrderService.getPurchaseRequestSimpleView(id).subscribe({
      next: (resp) => {
        this.hydrateFromSimpleView(resp);
        this.isLoading.set(false);
      },
      error: (err: unknown) => {
        this.isLoading.set(false);
        this.errorService.handleHttpError(err);
        this.router.navigate(['/purchase-requests']);
      }
    });
  }

  private hydrateFromSimpleView(resp: PurchaseRequestSimpleViewResponse): void {
    this.description.set((resp.description ?? '').toString());
    this.notes.set((resp.notes ?? '').toString());
    this.priorityKey.set(this.normalizePriorityKey((resp as unknown as { priority?: string }).priority));

    const ccId = resp.costCenterId ?? null;
    this.resolvedCostCenterId.set(ccId && ccId > 0 ? ccId : null);
    this.costCenterLabel.set(
      (resp.costCenterName || '').trim()
        ? (resp.costCenterName || '').trim()
        : ccId && ccId > 0
          ? 'Centro de custo'
          : null
    );

    const apiItems = Array.isArray(resp.items) ? resp.items : [];
    this.originalItemIds.clear();
    if (apiItems.length === 0) {
      this.itens.set([this.novoItemVazio()]);
      return;
    }
    const mapped = apiItems.map((it) => {
      const localId = this.gerarLocalId();
      const raw = it as unknown as Record<string, unknown>;
      const apiId = Number(raw['purchaseOrderItemId'] ?? raw['id'] ?? raw['Id']);
      if (Number.isFinite(apiId) && apiId > 0) {
        this.originalItemIds.set(localId, apiId);
      }
      return {
        id: localId,
        descricao: (it.productName ?? '').toString(),
        qtd: Number.isFinite(Number(it.quantityRequested)) ? Number(it.quantityRequested) : 1,
        unidade: (it.unit ?? 'UN').toString().toUpperCase(),
        justificativa: (it.justification ?? '').toString()
      } as SolicitacaoItem;
    });
    this.itens.set(mapped);
  }

  private normalizePriorityKey(priority: string | undefined | null): PriorityKey {
    const raw = (priority || '').trim().toLowerCase();
    if (raw === 'baixa' || raw === 'low') return 'baixa';
    if (raw === 'alta' || raw === 'high') return 'alta';
    if (raw === 'urgente' || raw === 'urgent') return 'urgente';
    return 'normal';
  }

  trackByItemId(_: number, item: SolicitacaoItem): string {
    return item.id;
  }

  setDescription(v: string): void {
    this.description.set(v);
  }

  setNotes(v: string): void {
    this.notes.set(v);
  }

  setPriorityKey(v: string): void {
    const allowed: PriorityKey[] = ['baixa', 'normal', 'alta', 'urgente'];
    const k = (v || 'normal').toLowerCase();
    if (allowed.includes(k as PriorityKey)) {
      this.priorityKey.set(k as PriorityKey);
    }
  }

  adicionarItem(): void {
    this.itens.update((lista) => [...lista, this.novoItemVazio()]);
  }

  removerItem(id: string): void {
    if (this.itens().length <= 1) {
      return;
    }
    this.itens.update((lista) => lista.filter((i) => i.id !== id));
  }

  patchItem(id: string, patch: Partial<Omit<SolicitacaoItem, 'id'>>): void {
    this.itens.update((lista) =>
      lista.map((i) => (i.id === id ? { ...i, ...patch } : i))
    );
  }

  onItemDescricao(id: string, ev: Event): void {
    this.patchItem(id, { descricao: (ev.target as HTMLInputElement).value });
  }

  onItemQtd(id: string, ev: Event): void {
    const n = Number((ev.target as HTMLInputElement).value);
    this.patchItem(id, { qtd: Number.isFinite(n) ? n : 1 });
  }

  onItemUnidade(id: string, ev: Event): void {
    this.patchItem(id, {
      unidade: (ev.target as HTMLInputElement).value.toUpperCase()
    });
  }

  onItemJustificativa(id: string, ev: Event): void {
    this.patchItem(id, {
      justificativa: (ev.target as HTMLTextAreaElement).value
    });
  }

  cancel(): void {
    this.router.navigate(['/purchase-requests']);
  }

  submit(): void {
    this.submitAttempted.set(true);
    if (!this.isFormValid()) {
      return;
    }

    if (this.isEditMode()) {
      this.submitUpdate();
      return;
    }

    const ccId = this.resolvedCostCenterId();
    if (!ccId) {
      this.error.set(
        'Não foi possível identificar o centro de custo desta solicitação. Peça ao administrador para conferir seu cadastro.'
      );
      return;
    }

    const payload: CreatePurchaseRequestPayload = {
      description: this.description().trim(),
      priority: this.priorityToApi[this.priorityKey()],
      notes: this.notes().trim() ? this.notes().trim() : undefined,
      items: this.itens().map((it) => ({
        productName: it.descricao.trim(),
        quantityRequested: it.qtd,
        unit: it.unidade.trim(),
        justification: it.justificativa.trim() ? it.justificativa.trim() : undefined
      }))
    };

    this.isSaving.set(true);
    this.error.set(null);
    this.purchaseOrderService.createPurchaseRequest(ccId, payload).subscribe({
      next: (res: unknown) => {
        this.isSaving.set(false);
        this.errorService.showSuccessMessage('Solicitação criada com sucesso');
        const r = res as Record<string, unknown>;
        const newId = r['id'] ?? r['Id'] ?? (r['data'] as Record<string, unknown>)?.['id'];
        if (newId != null && Number(newId) > 0) {
          this.router.navigate(['/purchase-requests', Number(newId)]);
        } else {
          this.router.navigate(['/purchase-requests']);
        }
      },
      error: (err: unknown) => {
        this.isSaving.set(false);
        this.errorService.handleHttpError(err);
      }
    });
  }

  private submitUpdate(): void {
    const id = this.editingId();
    if (!id) return;

    const items: UpdatePurchaseRequestItemPayload[] = this.itens().map((it) => {
      const purchaseOrderItemId = this.originalItemIds.get(it.id) ?? null;
      return {
        purchaseOrderItemId,
        productName: it.descricao.trim(),
        quantityRequested: it.qtd,
        unit: it.unidade.trim(),
        estimatedUnitPrice: null,
        justification: it.justificativa.trim() ? it.justificativa.trim() : null,
        itemId: null
      };
    });

    const payload: UpdatePurchaseRequestPayload = {
      description: this.description().trim(),
      priority: this.priorityToApi[this.priorityKey()],
      notes: this.notes().trim() ? this.notes().trim() : undefined,
      items
    };

    this.isSaving.set(true);
    this.error.set(null);
    this.purchaseOrderService.updatePurchaseRequest(id, payload).subscribe({
      next: () => {
        this.isSaving.set(false);
        this.errorService.showSuccessMessage('Solicitação atualizada com sucesso');
        this.router.navigate(['/purchase-requests', id]);
      },
      error: (err: unknown) => {
        this.isSaving.set(false);
        this.errorService.handleHttpError(err);
      }
    });
  }

  descricaoInvalida(item: SolicitacaoItem): boolean {
    return this.submitAttempted() && !item.descricao.trim();
  }

  qtdInvalida(item: SolicitacaoItem): boolean {
    return this.submitAttempted() && (item.qtd < 1 || !Number.isFinite(item.qtd));
  }

  unidadeInvalida(item: SolicitacaoItem): boolean {
    return this.submitAttempted() && !item.unidade.trim();
  }

  descricaoSolicitacaoInvalida(): boolean {
    return this.submitAttempted() && !this.description().trim();
  }

  private novoItemVazio(): SolicitacaoItem {
    return {
      id: this.gerarLocalId(),
      descricao: '',
      qtd: 1,
      unidade: 'UN',
      justificativa: ''
    };
  }

  private gerarLocalId(): string {
    return typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `i-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }

  private resolveCostCenterContext(): Observable<{
    id: number | null;
    label: string | null;
  }> {
    const fromToken = this.authService.getCostCenterIdFromToken();
    return this.authService.getStaffId().pipe(
      switchMap((sid) => {
        if (!sid) {
          return this.enrichLabelWithCostCenterApi(fromToken);
        }
        return this.staffService.getStaffById(sid, { suppressErrorToast: true }).pipe(
          switchMap((staff) => {
            const staffCc = this.extractCostCenterIdFromStaff(staff);
            const id = fromToken ?? staffCc;
            return this.enrichLabelWithCostCenterApi(id);
          }),
          catchError(() => this.enrichLabelWithCostCenterApi(fromToken))
        );
      })
    );
  }

  /**
   * Nome oficial do cadastro financeiro (`GET /costcenters/:id`), não só o texto do colaborador.
   */
  private enrichLabelWithCostCenterApi(
    id: number | null
  ): Observable<{ id: number | null; label: string | null }> {
    if (!id) {
      return of({ id: null, label: null });
    }
    return this.costCentersService.getById(id, { suppressErrorToast: true }).pipe(
      map((cc) => ({
        id,
        label: this.formatCostCenterLabel(cc.name, cc.code)
      })),
      catchError(() => of({ id, label: 'Centro de custo' }))
    );
  }

  /** Somente nome (ou código); sem exibir o id numérico na interface. */
  private formatCostCenterLabel(
    name: string | undefined,
    code: string | undefined
  ): string {
    const n = (name || '').trim();
    const c = (code || '').trim();
    if (n) {
      return n;
    }
    if (c) {
      return c;
    }
    return 'Centro de custo';
  }

  private extractCostCenterIdFromStaff(staff: StaffMember | Record<string, unknown>): number | null {
    const raw =
      (staff as Record<string, unknown>)?.['CostCenterId'] ??
      (staff as Record<string, unknown>)?.['costCenterId'];
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }
}
